import DOMPurify from 'dompurify';

/**
 * Rich text for Credit Proposal sections is stored as a small, sanitized HTML
 * subset produced by the shared RichTextEditor. Content saved before rich text
 * existed is plain text; every reader goes through these helpers so both kinds
 * keep rendering correctly (screen, report and Word export).
 */

/** Fonts offered in the editor — limited to fonts that also exist in Word. */
export const RICH_TEXT_FONT_FAMILIES = ['Calibri', 'Arial', 'Times New Roman', 'Georgia'] as const;

/** Font sizes offered in the editor (CSS px). */
export const RICH_TEXT_FONT_SIZES = ['12px', '14px', '16px', '20px'] as const;

/** Swatches offered for text colour. */
export const RICH_TEXT_COLORS = [
  '#111827',
  '#1E2A78',
  '#2563EB',
  '#059669',
  '#D97706',
  '#DC2626',
  '#7C3AED',
  '#6B7280',
] as const;

/** Swatches offered for highlight (background) colour. */
export const RICH_TEXT_HIGHLIGHTS = ['#FEF08A', '#BBF7D0', '#BFDBFE', '#FBCFE8', '#FED7AA'] as const;

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'span', 'mark', 'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];
const ALLOWED_ATTR = ['style', 'data-color', 'colspan', 'rowspan'];
const ALLOWED_STYLE_PROPERTIES = new Set(['color', 'background-color', 'font-family', 'font-size']);

/** True when the value is rich-text HTML (as opposed to legacy plain text). */
export function isRichTextHtml(value: unknown): value is string {
  return typeof value === 'string' && /^\s*<(p|ul|ol|table)[\s>]/i.test(value);
}

/** Sanitizes rich-text HTML down to the editor's formatting subset. */
export function sanitizeRichText(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_DOM: true,
  }) as HTMLElement;

  clean.querySelectorAll<HTMLElement>('[style]').forEach((element) => {
    const kept: string[] = [];

    for (let index = 0; index < element.style.length; index++) {
      const property = element.style.item(index);

      if (ALLOWED_STYLE_PROPERTIES.has(property)) {
        kept.push(`${property}: ${element.style.getPropertyValue(property)}`);
      }
    }

    if (kept.length) {
      element.setAttribute('style', kept.join('; '));
    } else {
      element.removeAttribute('style');
    }
  });

  return clean.innerHTML;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Converts legacy plain text to editor HTML: blank-line separated blocks become
 * paragraphs, single line breaks are kept as <br>.
 */
export function plainTextToRichText(text: string | null | undefined): string {
  const normalized = String(text ?? '').replace(/\r\n/g, '\n').trim();

  if (!normalized) {
    return '';
  }

  return normalized
    .split(/\n\s*\n/)
    .map((block) => `<p>${escapeHtml(block.trim()).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Legacy "one point per line" content (the old bullet mode of Highlights and
 * Key Risks) as an editor bullet list. Leading "•", "-" or "*" markers are
 * dropped; rich text is returned as it is.
 */
export function bulletLinesToRichText(value: string | string[] | null | undefined): string {
  if (!Array.isArray(value) && isRichTextHtml(value)) {
    return sanitizeRichText(value);
  }

  const items = (Array.isArray(value) ? value : String(value ?? '').split(/\r?\n/))
    .map((line) => String(line ?? '').replace(/^\s*[•*-]\s+/, '').trim())
    .filter(Boolean);

  return items.length
    ? `<ul>${items.map((item) => `<li><p>${escapeHtml(item)}</p></li>`).join('')}</ul>`
    : '';
}

/** Any stored value (rich or legacy plain text) as sanitized editor HTML. */
export function toRichText(value: string | null | undefined): string {
  return isRichTextHtml(value) ? sanitizeRichText(value) : plainTextToRichText(value);
}

/**
 * Plain-text projection of rich text: paragraphs separated by a blank line,
 * list items on their own line ("• " / "1. " prefixed), table rows on their
 * own line with cells separated by " | ". Used for the
 * plain-text column, validation and legacy consumers.
 */
export function richTextToPlainText(value: string | null | undefined): string {
  if (!isRichTextHtml(value)) {
    return String(value ?? '').trim();
  }

  const container = document.createElement('div');
  container.innerHTML = sanitizeRichText(value);

  const blocks: string[] = [];

  container.childNodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) {
      const text = node.textContent?.trim();
      if (text) blocks.push(text);
      return;
    }

    const tag = node.tagName.toLowerCase();

    if (tag === 'ul' || tag === 'ol') {
      const items = Array.from(node.children).map((item, index) => {
        const marker = tag === 'ol' ? `${index + 1}. ` : '• ';
        return marker + textWithBreaks(item).trim();
      });
      blocks.push(items.join('\n'));
      return;
    }

    if (tag === 'table') {
      const rows = Array.from(node.querySelectorAll('tr')).map((row) =>
        Array.from(row.children)
          .map((cell) => textWithBreaks(cell).replace(/\s+/g, ' ').trim())
          .join(' | '),
      );
      blocks.push(rows.join('\n'));
      return;
    }

    blocks.push(textWithBreaks(node).trim());
  });

  return blocks.filter(Boolean).join('\n\n');
}

function textWithBreaks(element: Element): string {
  let text = '';

  element.childNodes.forEach((child) => {
    if (child instanceof HTMLBRElement) {
      text += '\n';
    } else if (child instanceof Element) {
      text += textWithBreaks(child);
    } else {
      text += child.textContent ?? '';
    }
  });

  return text;
}

/**
 * Text of a paragraph-type block (blkTyp 1, txtData column). Rich text is
 * stored in jsonData[0].content — which the report and Word export already
 * read first — while txtData keeps a plain-text copy; blocks saved before
 * rich text only have txtData.
 */
export function readParagraphBlockText(row: { txtData?: unknown; jsonData?: unknown } | null | undefined): string {
  const richContent = Array.isArray(row?.jsonData) ? row.jsonData[0]?.content : null;

  return isRichTextHtml(richContent) ? richContent : String(row?.txtData ?? '');
}

/** txtData / jsonData pair to save for a paragraph-type block (see readParagraphBlockText). */
export function paragraphBlockData(value: string | null | undefined): {
  txtData: string;
  jsonData: Array<{ content: string }>;
} {
  return {
    txtData: richTextToPlainText(value),
    jsonData: isRichTextHtml(value) ? [{ content: sanitizeRichText(value) }] : [],
  };
}

/** Value to persist for an editor field: sanitized HTML, or legacy plain text as-is. */
export function richTextForSave(value: string | null | undefined): string {
  return isRichTextHtml(value) ? sanitizeRichText(value) : String(value ?? '');
}

/**
 * Joins several stored paragraphs into one value. When any of them is rich
 * text, all are converted to HTML so a mix of legacy plain text and new rich
 * paragraphs still renders as a single valid rich value.
 */
export function joinRichText(values: Array<string | null | undefined>): string {
  const present = values.map((value) => String(value ?? '').trim()).filter(Boolean);

  return present.some(isRichTextHtml)
    ? present.map((value) => toRichText(value)).join('')
    : present.join('\n\n');
}

/** True when rich or plain content has visible text. */
export function hasRichTextContent(value: string | null | undefined): boolean {
  return richTextToPlainText(value).trim().length > 0;
}
